// Loaded once per page even though every policy section instance prints its own <script> tag.
(function () {
  if (window.__fltcPolicyPageLoaded) return;
  window.__fltcPolicyPageLoaded = true;
// Global state for quick jump functionality
  window.policyQuickJumpState = window.policyQuickJumpState || {
    initialized: false,
    menu: null,
    items: [],
    observers: new Map(),
    activeSection: null,
    instances: new Set()
  };

  class PolicySection {
    constructor(container) {
      this.container = container;
      this.sectionId = container.id;
      this.collapsibles = this.container.querySelectorAll('.policy-section__collapsible');
      
      // Store reference to global state
      this.quickJumpState = window.policyQuickJumpState;
      
      // Register this instance
      this.quickJumpState.instances.add(this);
      
      // Initialize instance-specific events
      this.eventHandlers = [];

      this.init();
    }

    init() {
      this.initCollapsibles();
      
      // Only initialize global quick jump menu once
      if (!this.quickJumpState.initialized) {
        this.initGlobalQuickJump();
        this.quickJumpState.initialized = true;
      }
      
      // Always register this section's items
      this.registerQuickJumpItems();
    }

    initGlobalQuickJump() {
      this.createQuickJumpMenu();
      this.initGlobalEventHandlers();
      this.initIntersectionObserver();
    }

    createQuickJumpMenu() {
      let quickJumpMenu = document.getElementById('GlobalPolicyQuickJump');
      
      if (!quickJumpMenu) {
        quickJumpMenu = document.createElement('div');
        quickJumpMenu.id = 'GlobalPolicyQuickJump';
        quickJumpMenu.className = 'policy-section__quick-jump';
        quickJumpMenu.setAttribute('aria-label', 'Quick navigation');
        
        quickJumpMenu.innerHTML = `
          <div class="quick-jump__wrapper">
            <button class="quick-jump__toggle" aria-expanded="false">
              <span>Quickly Jump To:</span>
              <span class="quick-jump__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M18 15L12 9L6 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
            <div class="quick-jump__content">
              <nav class="quick-jump__nav"></nav>
            </div>
          </div>
        `;
        
        document.body.appendChild(quickJumpMenu);
        this.quickJumpState.menu = quickJumpMenu;
      } else {
        this.quickJumpState.menu = quickJumpMenu;
      }

      this.quickJumpState.toggle = quickJumpMenu.querySelector('.quick-jump__toggle');
      this.quickJumpState.content = quickJumpMenu.querySelector('.quick-jump__content');
      this.quickJumpState.nav = quickJumpMenu.querySelector('.quick-jump__nav');
      this.quickJumpState.isOpen = false;
      this.quickJumpState.visible = false;
      this.quickJumpState.scrollThreshold = 150;
    }

    initGlobalEventHandlers() {
      // Toggle menu when clicking the button
      this.quickJumpState.toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleQuickJump(!this.quickJumpState.isOpen);
      });
      
      // Close when clicking outside
      document.addEventListener('click', (event) => {
        if (this.quickJumpState.isOpen && !this.quickJumpState.content.contains(event.target)) {
          this.toggleQuickJump(false);
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.quickJumpState.isOpen) {
          this.toggleQuickJump(false);
        }
      });

      // Handle scroll to show/hide menu
      window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        const shouldShow = currentScroll > this.quickJumpState.scrollThreshold;
        
        if (shouldShow !== this.quickJumpState.visible) {
          this.quickJumpState.visible = shouldShow;
          this.quickJumpState.menu.classList.toggle('psqj-visible', shouldShow);
          
          if (!shouldShow) {
            this.toggleQuickJump(false);
          }
        }
      });

      // Set up smooth scrolling
      this.quickJumpState.nav.addEventListener('click', (event) => {
        const link = event.target.closest('.quick-jump__link');
        if (!link) return;
        
        event.preventDefault();
        
        // Remove active class from all links
        const allLinks = this.quickJumpState.nav.querySelectorAll('.quick-jump__link');
        allLinks.forEach(l => l.classList.remove('is-active'));
        
        // Add active class to clicked link
        link.classList.add('is-active');
        
        const targetId = link.getAttribute('href');
        if (!targetId) return;

        const targetElement = document.querySelector(targetId);
        if (!targetElement) return;

        const headerOffset = parseInt(getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')) || 80;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        this.toggleQuickJump(false);
      });
    }

    initIntersectionObserver() {
      // Clean up any existing observers
      if (this.quickJumpState.observers.size > 0) {
        this.quickJumpState.observers.forEach(observer => observer.disconnect());
        this.quickJumpState.observers.clear();
      }

      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -20% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      };

      // Create a single observer for all sections
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
            const targetId = entry.target.id;
            if (!targetId) return;
            
            const links = this.quickJumpState.nav.querySelectorAll('.quick-jump__link');
            links.forEach(link => {
              const linkTargetId = link.getAttribute('href')?.substring(1);
              if (linkTargetId === targetId) {
                // Remove active class from all links
                links.forEach(l => l.classList.remove('is-active'));
                // Add active class to current link
                link.classList.add('is-active');
              }
            });
          }
        });
      }, observerOptions);

      // Store the observer for later cleanup
      this.quickJumpState.observers.set('main', observer);

      // Observe all sections across the page
      document.querySelectorAll('[id^="heading-"], [id^="shipping-"], [id^="tab-"], [id^="contact-"]').forEach(section => {
        observer.observe(section);
      });
    }

    toggleQuickJump(show) {
      this.quickJumpState.isOpen = show;
      this.quickJumpState.toggle.setAttribute('aria-expanded', show);
      this.quickJumpState.content.classList.toggle('is-active', show);
      
      if (show) {
        // Ensure menu is visible within viewport
        const menuRect = this.quickJumpState.content.getBoundingClientRect();
        if (menuRect.top < 0) {
          this.quickJumpState.content.style.bottom = 'auto';
          this.quickJumpState.content.style.top = '100%';
          this.quickJumpState.content.style.marginTop = '8px';
          this.quickJumpState.content.style.marginBottom = '0';
        }
      }
    }

    registerQuickJumpItems() {
      // Collect items from this section
      let sectionItems = [];
      
      // Get all headings, tables, tabs, and contacts with IDs
      const blocks = this.container.querySelectorAll('[id^="heading-"], [id^="shipping-"], [id^="tab-"], [id^="contact-"]');
      
      blocks.forEach(block => {
        let title = '';
        
        // Extract title based on block type
        if (block.id.startsWith('heading-')) {
          title = block.querySelector('h1, h2, h3')?.textContent || 'Section';
        } else if (block.id.startsWith('shipping-')) {
          title = block.querySelector('.shipping-table__title')?.textContent || 'Shipping';
        } else if (block.id.startsWith('tab-')) {
          title = block.querySelector('.collapsible__title')?.textContent || 'Details';
        } else if (block.id.startsWith('contact-')) {
          title = block.querySelector('.contact__title')?.textContent || 'Contact';
        }
        
        sectionItems.push({
          id: block.id,
          title: title.trim(),
          sectionId: this.sectionId
        });
      });
      
      // Add items to global registry
      this.quickJumpState.items = this.quickJumpState.items
        .filter(item => item.sectionId !== this.sectionId) // Remove old items from this section
        .concat(sectionItems); // Add new items
      
      // Update the menu with all items
      this.updateQuickJumpMenu();
    }

    updateQuickJumpMenu() {
      if (!this.quickJumpState.nav) return;
      
      // Clear existing links
      this.quickJumpState.nav.innerHTML = '';
      
      // Create links for all registered items
      this.quickJumpState.items.forEach(item => {
        const link = document.createElement('a');
        link.href = `#${item.id}`;
        link.className = 'quick-jump__link';
        link.textContent = item.title;
        link.setAttribute('data-section-id', item.sectionId);
        this.quickJumpState.nav.appendChild(link);
      });
    }

    initCollapsibles() {
      this.collapsibles.forEach(collapsible => {
        const trigger = collapsible.querySelector('.collapsible__trigger');
        const content = collapsible.querySelector('.collapsible__content');

        if (!trigger || !content) return;

        // Set initial ARIA states
        const isOpen = trigger.classList.contains('is-open');
        trigger.setAttribute('aria-expanded', isOpen);
        content.setAttribute('aria-hidden', !isOpen);

        if (isOpen) {
          content.style.display = 'block';
          content.style.height = 'auto';
        } else {
          content.style.display = 'none';
          content.style.height = '0';
        }

        // Remove any existing event listeners to prevent duplicates
        if (trigger.clickHandler) {
          trigger.removeEventListener('click', trigger.clickHandler);
        }
        if (trigger.keydownHandler) {
          trigger.removeEventListener('keydown', trigger.keydownHandler);
        }

        // Create event handlers
        trigger.clickHandler = (event) => {
          event.preventDefault();
          this.toggleCollapsible(trigger, content);
        };
        trigger.keydownHandler = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleCollapsible(trigger, content);
          }
        };

        // Add event listeners
        trigger.addEventListener('click', trigger.clickHandler);
        trigger.addEventListener('keydown', trigger.keydownHandler);

        // Store handlers for later removal
        this.eventHandlers.push({ element: trigger, type: 'click', handler: trigger.clickHandler });
        this.eventHandlers.push({ element: trigger, type: 'keydown', handler: trigger.keydownHandler });

        // Initialize transitioning flag
        collapsible.isTransitioning = false;
      });
    }

    toggleCollapsible(trigger, content) {
      const collapsible = trigger.closest('.policy-section__collapsible');

      if (collapsible.isTransitioning) return; // Prevent toggle during transition
      collapsible.isTransitioning = true;

      const isOpen = trigger.classList.contains('is-open');

      // Toggle classes and ARIA attributes
      trigger.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', !isOpen);
      content.setAttribute('aria-hidden', isOpen);

      if (isOpen) {
        // Closing
        content.style.height = `${content.scrollHeight}px`; // Set to current height
        requestAnimationFrame(() => {
          content.style.height = '0';
        });

        const onTransitionEnd = (event) => {
          if (event.propertyName !== 'height') return;
          content.style.display = 'none';
          content.removeEventListener('transitionend', onTransitionEnd);
          collapsible.isTransitioning = false;
        };

        content.addEventListener('transitionend', onTransitionEnd);
      } else {
        // Opening
        content.style.display = 'block';
        content.style.height = '0'; // Reset height to 0
        requestAnimationFrame(() => {
          content.style.height = `${content.scrollHeight}px`; // Set to full height
        });

        const onTransitionEnd = (event) => {
          if (event.propertyName !== 'height') return;
          content.style.height = 'auto'; // Allow height to adjust naturally
          content.removeEventListener('transitionend', onTransitionEnd);
          collapsible.isTransitioning = false;
        };

        content.addEventListener('transitionend', onTransitionEnd);
      }
    }

    refresh() {
      this.registerQuickJumpItems();
      this.initCollapsibles();
    }

    destroy() {
      // Remove event listeners
      this.eventHandlers.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
      this.eventHandlers = [];

      // Remove this instance from registry
      this.quickJumpState.instances.delete(this);
      
      // Remove items from this section
      this.quickJumpState.items = this.quickJumpState.items.filter(item => item.sectionId !== this.sectionId);
      
      // Update menu if other instances still exist
      if (this.quickJumpState.instances.size > 0) {
        this.updateQuickJumpMenu();
      } else {
        // Clean up global resources if this is the last instance
        this.quickJumpState.observers.forEach(observer => observer.disconnect());
        this.quickJumpState.observers.clear();
        
        // Remove menu from DOM if needed
        if (this.quickJumpState.menu && this.quickJumpState.menu.parentNode) {
          this.quickJumpState.menu.parentNode.removeChild(this.quickJumpState.menu);
        }
        
        // Reset global state
        this.quickJumpState.initialized = false;
        this.quickJumpState.menu = null;
        this.quickJumpState.items = [];
      }
    }
  }

  // Initialize all policy sections
  let policySectionInstances = new Map();

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.policy-section').forEach(section => {
      const instance = new PolicySection(section);
      policySectionInstances.set(section, instance);
    });
  });

  // Handle Shopify section events
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.classList.contains('policy-section')) {
      const instance = policySectionInstances.get(event.target);
      if (instance) {
        instance.destroy();
        policySectionInstances.delete(event.target);
      }
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('policy-section')) {
      const instance = new PolicySection(event.target);
      policySectionInstances.set(event.target, instance);
    }
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    if (event.target.classList.contains('policy-section')) {
      const instance = policySectionInstances.get(event.target);
      if (instance) {
        instance.refresh();
      } else {
        const newInstance = new PolicySection(event.target);
        policySectionInstances.set(event.target, newInstance);
      }
    }
  });

  // Support for Shopify's theme editor
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const collapsible = event.target.closest('.policy-section__collapsible');
      if (collapsible) {
        const trigger = collapsible.querySelector('.collapsible__trigger');
        const content = collapsible.querySelector('.collapsible__content');
        if (trigger && content && !trigger.classList.contains('is-open')) {
          trigger.click();
        }
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const collapsible = event.target.closest('.policy-section__collapsible');
      if (collapsible) {
        const trigger = collapsible.querySelector('.collapsible__trigger');
        const content = collapsible.querySelector('.collapsible__content');
        if (trigger && content && trigger.classList.contains('is-open')) {
          trigger.click();
        }
      }
    });
  }
})();
